import * as React from 'react';
import { styled } from '@glitz/react';
import {
  translate,
  useDynamicData,
  offsetTop,
  scrollTo,
  getComponentState,
  setComponentState,
  historyEvent,
  HistoryEventType,
  useMatchMobile,
  currentUrl,
} from '@avensia/excite';
import { LoadingProductList } from 'webapp/product/ProductList';
import {
  createMaxSmallViewportDecorator,
  createMinLargeViewportDecorator,
  createMinMediumViewportDecorator,
} from 'webapp/viewport';
import { ProductInformation, AddToCart } from './ProductInformation';
import { ProductImagesGalleryProvider } from './ProductImages';
import ContentList from './BaseContentList';
import ProductListCarousel from './ProductListCarousel';
import { StoreAvailabilityFlyout } from './StoreAvailabilityFlyout';
import ProductDetails from './ProductDetails';
import { ShippingInfoFlyout } from './ShippingInfoFlyout';
import { PriceMatchInfoFlyout } from './PriceMatchFlyout';
import { VariantMobileOptions } from './MobileVariantSelector';
import { VariantSelectorsProvider, useVariantSelector } from 'webapp/variant-selectors/ProductVariantsSelectorProvider';
import { productConfigurationTransform, variantTransform } from './ProductVariantsSelector';
import { StickyCrossSell, UpSellAccessories } from './UpSellAccessories';
import { CevoidBlock } from 'webapp/blocks/CevoidBlock';
import Cylindo from 'webapp/product/Cylindo';
import ProductInformationHeader from './ProductInformationHeader';
import { ArrowRight } from 'webapp/components/icons';
import { Theme } from 'webapp/theme';
import { BreadCrumbs } from 'webapp/components/Breadcrumbs';
import { ArticleListing } from './ArticleListing';
import { desktopEdgeMarginDecorator, maxContentNormalWidthDecorator } from 'webapp/components/page-layout';
import ProductImageCarousel from './ProductImageCarousel';
import { ZIndex } from 'webapp/z-index';
import { DESKTOP_HEADER_HEIGHT, HEADER_HEIGHT } from 'webapp/utils/constants';
import { Button } from 'webapp/components/button';
import { FieldSize } from 'webapp/components/field-size';
import { addSelectContentEventToDataLayer } from 'webapp/tracking/events/content';
import Link from 'webapp/components/Link';
import Reviews from './Reviews';
import { secondaryGrey } from 'webapp/shared/sharedTheme';
import { registerComponent } from '@avensia/excite';
import ProductBadgesFlyout from './ProductBadgesFlyout';
import { TestIdProps } from '@avensia/test-automation';
import { useNinetailedFeatureTest } from 'webapp/ninetailed/NinetailedProvider';
import { useRetailMedia } from 'webapp/components/retail-media/use-retail-media';

export default registerComponent<NordicNest.Api.ProductPageApiModel>(function ProcuctPage(product) {
  const [cevoid] = useDynamicData<NordicNest.Api.ProductTemplatePage, NordicNest.Api.CevoidBlock | undefined>({
    selector: data => data?.cevoid,
  });
  const [recommendations] = useDynamicData<NordicNest.Api.ProductPageRecommendationsApiModel>({
    context: product.recommendationsContext,
  });

  const [productBadgesApiModel] = useDynamicData<NordicNest.Api.ProductBadgesApiModel>({
    context: product.productBadgesContext,
  });

  const retailMediaContent = useRetailMedia(
    'product',
    undefined,
    undefined,
    product.sku,
    [],
    product.retailMediaEnabled,
  );

  const hasGclid = currentUrl().searchParams.has('gclid');
  const { variantKey, componentRef } = useNinetailedFeatureTest<HTMLDivElement>('sticky-cross-sell-panel', hasGclid);

  const accessories = recommendations?.productAccessories;
  const accessoryProducts = accessories?.map(item => item.accessoryProduct);
  const articlePages = recommendations?.articlePages;
  const isMobile = useMatchMobile();
  const reviewsContainerRef = React.useRef<HTMLDivElement>(null);
  const galleryContainerRef = React.useRef<HTMLDivElement>(null);
  const specificationsContainerRef = React.useRef<HTMLDivElement>(null);
  const accordionTitleHeight = 56 / 2;
  const headerHeight = isMobile ? HEADER_HEIGHT : DESKTOP_HEADER_HEIGHT + accordionTitleHeight;
  const [openAccordion, setOpenAccordion] = React.useState(
    getComponentState<undefined | string>(product.sku + '-accordion', `${product.sku}-product-description`),
  );

  const scrollToReviews = React.useCallback(() => {
    if (reviewsContainerRef?.current && reviewsContainerRef.current?.hasChildNodes()) {
      if (reviewsContainerRef?.current) {
        scrollTo(offsetTop(reviewsContainerRef.current) - headerHeight);
      }
    }
  }, [headerHeight]);

  const scrollToSpecifications = React.useCallback(() => {
    setOpenAccordion(`${product.sku}-product-specification`);
    setTimeout(() => {
      if (specificationsContainerRef.current) {
        scrollTo(offsetTop(specificationsContainerRef.current) - (headerHeight + accordionTitleHeight));
      }
    }, 200);
  }, [accordionTitleHeight, headerHeight, product.sku]);
  const [accessoriesToCart, setAccessoriesToCart] = React.useState<
    (NordicNest.Api.PartialProductApiModel & { quantity: number })[]
  >([]);
  const updateAccessoriesAdded = React.useCallback(
    (accessories: (NordicNest.Api.PartialProductApiModel & { quantity: number })[], remove?: boolean) => {
      if (remove) {
        setAccessoriesToCart(accessoriesToCart.filter(t => !accessories.find(a => t.productId === a.productId)));
      } else {
        setAccessoriesToCart([...accessoriesToCart, ...accessories]);
      }
    },
    [accessoriesToCart],
  );

  const [openShippingInfo, setOpenShippingInfo] = React.useState(false);
  const [openPriceMatchInfo, setOpenPriceMatchInfo] = React.useState(false);
  const [openStoreAvailability, setOpenStoreAvailability] = React.useState(false);
  const [openUpsellAccessories, setOpenUpsellAccessories] = React.useState(false);
  const [openStickyCrossSell, setOpenStickyCrossSell] = React.useState(true);
  const [productBadgesFlyoutOpen, setProductBadgesFlyoutOpen] = React.useState(false);
  const upsellAccessoriesHasBeenShownRef = React.useRef<boolean>(false);

  React.useEffect(
    () =>
      historyEvent.on(e => {
        if (e.event === HistoryEventType.Push) {
          setOpenUpsellAccessories(false);
          setComponentState({ id: 'upsell', state: false });
        }
        if (e.event === HistoryEventType.Pop) {
          setOpenUpsellAccessories(getComponentState<boolean>('upsell', false));
        }
      }),
    [openUpsellAccessories],
  );

  React.useEffect(() => {
    //Reset accessoriesToCart when product changes
    setAccessoriesToCart([]);
  }, [product.sku]);

  const showUpSellAccessories = React.useCallback(() => {
    if (accessories && accessories.length > 0) {
      upsellAccessoriesHasBeenShownRef.current = true;
      setOpenUpsellAccessories(true);
      setComponentState({ id: 'upsell', state: true });
      scrollTo(0);
    }
  }, [accessories]);

  const closeUpSellAccessories = React.useCallback(() => {
    setOpenUpsellAccessories(false);
    setComponentState({ id: 'upsell', state: false });
  }, []);

  const stickyCrossSell = (
    <StickyCrossSell
      sku={product.sku}
      products={(recommendations?.relatedProductsCrossSell ?? []).slice(0, 4)}
      visible={openStickyCrossSell}
      close={() => setOpenStickyCrossSell(false)}
    />
  );

  const upSellAccessories = accessories && (
    <UpSellAccessories
      key={product.sku}
      sku={product.sku}
      visible={openUpsellAccessories}
      accessories={accessories}
      close={closeUpSellAccessories}
    />
  );

  const imageGallery = (
    <GalleryContainer ref={galleryContainerRef}>
      <StyledBreadCrumbs isProductPage {...product.breadcrumbs} />
      {product.productConfiguration ? (
        <Cylindo config={product.productConfiguration} />
      ) : (
        <ProductImageCarousel product={product} packageKey={product.packageKey} />
      )}
    </GalleryContainer>
  );

  const productBadges = productBadgesApiModel?.productBadges ?? [];

  const productInformationHeader = <ProductInformationHeader scrollToReviews={scrollToReviews} {...product} />;

  const productInformation = (
    <ProductInformation
      showShippingInfo={() => setOpenShippingInfo(true)}
      showPriceMatchInfo={() => setOpenPriceMatchInfo(true)}
      showStoreAvailability={() => setOpenStoreAvailability(true)}
      showProductBadgesFlyout={() => setProductBadgesFlyoutOpen(true)}
      productBadges={productBadges}
      openUpSellAccessories={showUpSellAccessories}
      scrollToSpecifications={scrollToSpecifications}
      showStoreView={product.showStore}
      accessories={accessories}
      accessoriesToCart={accessoriesToCart}
      updateAccessoriesAdded={updateAccessoriesAdded}
      materialSamples={recommendations?.materialSamples}
      {...product}
    />
  );

  const bottomContent = (
    <>
      <ProductDetails
        {...product}
        accessories={accessoryProducts}
        spareParts={recommendations?.spareParts}
        openAccordion={openAccordion}
        setOpenAccordion={setOpenAccordion}
        specificationsContainerRef={specificationsContainerRef}
      />
      <Reviews
        ref={reviewsContainerRef}
        key={product.productId + product.sku}
        testFreaksFamilyId={product.productId}
        testFreaksProductId={product.sku}
      />
      <ContentList
        showMoreLink={''}
        showMoreText={translate(t => t.productPage.relateProductsCrossSellShowMoreText)}
        heading={translate(t => t.productPage.relateProductsCrossSellHeading)}
        backgroundColor="white"
        centerHeader={false}
      >
        {recommendations ? (
          <ProductListCarousel
            id={'pdp_crosssell_' + product.sku}
            name="pdp_crosssell"
            products={recommendations.relatedProductsCrossSell.slice(0, 4)}
          />
        ) : (
          <LoadingProductList count={4} />
        )}
      </ContentList>
      {recommendations?.productsFromBrand && recommendations.productsFromBrand.length > 0 && (
        <ContentList
          showMoreLink={product.brandUrl}
          showMoreText={translate(t => t.productPage.showAllFromBrand, { brand: product.brand })}
          heading={translate(t => t.productPage.moreFromBrand, { ['brand']: product.brand })}
          backgroundColor="white"
          centerHeader={false}
        >
          {recommendations && (
            <ProductListCarousel
              id={'pdp_productlist_brand_' + product.sku}
              name="pdp_productlist_brand"
              products={recommendations.productsFromBrand.slice(0, 4)}
            />
          )}
        </ContentList>
      )}
      {recommendations?.productsFromCollection && recommendations.productsFromCollection.length > 0 && (
        <ContentList
          showMoreLink={product.collectionUrl}
          showMoreText={translate(t => t.productPage.showAllFromCollection, { collection: product.collection })}
          heading={translate(t => t.productPage.moreFromCollection, { collection: product.collection })}
          backgroundColor="white"
          centerHeader={false}
        >
          <ProductListCarousel
            id={'pdp_productlist_collection_' + product.sku}
            name="pdp_productlist_collection"
            products={recommendations.productsFromCollection.slice(0, 4)}
          />
        </ContentList>
      )}
      {retailMediaContent?.sponsoredProducts && retailMediaContent?.sponsoredProducts.length > 0 && (
        <ContentList
          heading={translate(t => t.productPage.sponsoredProductsHeadline)}
          backgroundColor="white"
          centerHeader={false}
        >
          {retailMediaContent.sponsoredProducts ? (
            <ProductListCarousel
              id={'pdp_sponsored_products_' + product.sku}
              name="pdp_sponsored_products"
              products={retailMediaContent.sponsoredProducts.slice(0, 4)}
            />
          ) : (
            <LoadingProductList count={4} />
          )}
        </ContentList>
      )}
      {articlePages && articlePages?.length > 0 && (
        <ContentList
          showMoreLink={recommendations?.inspirationUrl}
          showMoreText={translate(t => t.productPage.articlePages)}
          heading={translate(t => t.productPage.moreArticles)}
          backgroundColor="white"
          centerHeader={false}
        >
          <ArticleListing productId={product.productId} list={articlePages} />
        </ContentList>
      )}
      {cevoid && <CevoidBlock {...cevoid} productId={product.sku} css={cevoidDecorator} centerHeader={false} />}
      {product.productListingPagesLinks && product.productListingPagesLinks.length > 0 && (
        <DiscoverSection>
          <DiscoverHeader>{translate(t => t.productPage.exploreHeading)}</DiscoverHeader>
          <DiscoverMore>
            {product.productListingPagesLinks?.map(l => (
              <DiscoverLink
                key={l.url}
                to={l.url}
                onClick={() => {
                  addSelectContentEventToDataLayer({
                    id: '',
                    link: l.url,
                    type: 'link',
                    content: '',
                    action: '',
                  });
                }}
              >
                {translate(t => t.productPage.exploreRootCategoryLabel, {
                  rootCategory: l.name,
                })}
              </DiscoverLink>
            ))}
          </DiscoverMore>
        </DiscoverSection>
      )}
      <ShippingInfoFlyout open={openShippingInfo} setOpen={setOpenShippingInfo} />
      <PriceMatchInfoFlyout open={openPriceMatchInfo} setOpen={setOpenPriceMatchInfo} />
      <ProductBadgesFlyout
        productBadges={productBadges}
        productName={product.displayName}
        open={productBadgesFlyoutOpen}
        setOpen={setProductBadgesFlyoutOpen}
      />
      <StoreAvailabilityFlyout
        storesAvailability={product.storesAvailability}
        open={openStoreAvailability}
        setOpen={setOpenStoreAvailability}
      />
      <VariantMobileOptions />
    </>
  );

  return (
    <ProductImagesGalleryProvider>
      <VariantSelectorsProvider
        key={product.sku}
        showVariantSelect={product.hasManyVaraints}
        currentVariant={product.variants?.find(v => v.articleNumber === product.sku)}
        productConfigurationTransform={productConfigurationTransform}
        variantTransform={variantTransform}
        {...product}
      >
        <div ref={componentRef}>
          {variantKey === 'show-sticky-cross-sell' &&
            hasGclid &&
            !upsellAccessoriesHasBeenShownRef.current &&
            stickyCrossSell}
        </div>

        {upSellAccessories}
        {isMobile ? (
          <div {...TestIdProps.ProductDetailPage}>
            <div>
              {imageGallery}
              {productInformationHeader}
            </div>
            <div>
              {productInformation}
              {bottomContent}
              <StickyAddToCart
                {...product}
                accessoriesToCart={accessoriesToCart}
                showUpSellAccessories={showUpSellAccessories}
              />
            </div>
          </div>
        ) : (
          <>
            <ProductContainer {...TestIdProps.ProductDetailPage}>
              {imageGallery}
              <ProductInformationWrapper>
                {productInformationHeader}
                {productInformation}
              </ProductInformationWrapper>
            </ProductContainer>
            {bottomContent}
          </>
        )}
      </VariantSelectorsProvider>
    </ProductImagesGalleryProvider>
  );
});

const StickyAddToCart = (
  props: NordicNest.Api.ProductPageApiModel & {
    showUpSellAccessories: () => void;
    accessoriesToCart: (NordicNest.Api.PartialProductApiModel & { quantity: number })[];
  },
) => {
  const { open } = useVariantSelector();
  const addToCartStickyRef = React.useRef<HTMLDivElement>(null);
  const [isSticky, setIsSticky] = React.useState<boolean>(false);

  //This setup is so that the add to cart can be above third party widgets while sticky
  //and still be below the header while not sticky
  React.useEffect(() => {
    const stickyEl = addToCartStickyRef.current;
    const observer = new IntersectionObserver(
      ([entry]: IntersectionObserverEntry[]) => {
        if (entry.intersectionRect.bottom >= 667) {
          setIsSticky(true);
        } else {
          setIsSticky(false);
        }
      },
      { threshold: [0, 0.1, 1] },
    );
    if (stickyEl) {
      observer.observe(stickyEl);
    }
    return () => {
      if (stickyEl) {
        observer.unobserve(stickyEl);
      }
    };
  }, []);

  return (
    <MobileAddToCart css={!isSticky && lowZIndexDecorator} ref={addToCartStickyRef}>
      <AddToCart
        {...props}
        accessoriesToCart={props.accessoriesToCart}
        openUpSellAccessories={props.showUpSellAccessories}
      />
      <MobileVariantSelector
        as={Theme.White}
        size={FieldSize.XSmall}
        onClick={() => {
          open('default');
        }}
        disableRippleEffect
      >
        {props.variantName}
        <StyledArrow />
      </MobileVariantSelector>
    </MobileAddToCart>
  );
};

const ProductContainer = styled.div(
  maxContentNormalWidthDecorator,
  desktopEdgeMarginDecorator,
  createMinLargeViewportDecorator({
    gridColumnGap: t => t.sharedSpacing.xl,
    gridTemplateColumns: '7fr 392px',
  }),
  createMinMediumViewportDecorator({
    display: 'grid',
    gridRowGap: 60,
    alignItems: 'start',
  }),
  createMaxSmallViewportDecorator({
    gridColumnGap: t => t.sharedSpacing.xs,
    padding: { x: 0 },
    gridTemplateColumns: '1.5fr 1fr',
  }),
);

const cevoidDecorator = styled(
  createMinLargeViewportDecorator({
    padding: { x: t => t.sharedSpacing.xxl, bottom: 128 },
  }),
  {
    padding: { x: t => t.sharedSpacing.m, bottom: 64 },
  },
);

const StyledBreadCrumbs = styled(
  BreadCrumbs,
  createMinLargeViewportDecorator({
    padding: { left: 0, right: t => t.sharedSpacing.xl },
    margin: { y: t => t.sharedSpacing.m },
  }),
  {
    margin: { y: t => t.sharedSpacing.m },
  },
);

const DiscoverSection = styled.div(
  maxContentNormalWidthDecorator,
  createMinLargeViewportDecorator({
    padding: { x: t => t.sharedSpacing.xxl },
  }),
  { padding: { x: t => t.sharedSpacing.m } },
);

const DiscoverHeader = styled.h2(
  createMinLargeViewportDecorator({
    margin: { bottom: t => t.sharedSpacing.xl },
  }),
  {
    fontSize: t => t.sharedFontSize.l,
    fontWeight: t => t.fontWeight?.bold,
    letterSpacing: 0,
    margin: { x: 'auto', bottom: t => t.sharedSpacing.l },
  },
);

const DiscoverMore = styled.div(
  createMinLargeViewportDecorator({
    maxWidth: 564,
  }),
  {
    display: 'flex',
    flexDirection: 'column',
    margin: { x: 'auto', bottom: t => t.sharedSpacing.xxxl },
  },
);

const DiscoverLink = styled(Link, {
  padding: { x: t => t.sharedSpacing.m, y: t => t.sharedSpacing.xs },
  fontSize: t => t.sharedFontSize.xs,
  border: { top: { color: secondaryGrey, style: 'solid', width: '1px' } },
  ':hover': {
    textDecoration: 'underline',
  },
});

const ProductInformationWrapper = styled.div({
  zIndex: 0,
});

const MobileAddToCart = styled.div({
  padding: { xy: t => t.sharedSpacing.m },
  backgroundColor: 'white',
  borderTop: { style: 'solid', width: 1, color: t => t.borderStrongColor },
  position: 'sticky',
  bottom: -1,
  zIndex: ZIndex.StickyAddToCart,
});

const MobileVariantSelector = styled(Button, {
  display: 'flex',
  justifyContent: 'space-between',
  width: '100%',
  height: 41,
  fontSize: t => t.sharedFontSize.xs,
  fontWeight: 400,
  border: { xy: { color: t => t.borderStrongColor } },
});

const StyledArrow = styled(ArrowRight, {
  fontSize: t => t.sharedFontSize.xxs,
  strokeWidth: 1.5,
});

const lowZIndexDecorator = styled({ zIndex: 2 });
const GalleryContainer = styled.div({
  height: '100%',
});
